import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { userApi } from "../lib/api";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const loadCount = () => userApi.unreadCount().then((r) => setUnread(r.count)).catch(() => {});
  const loadItems = () => userApi.notifications().then(setItems).catch(() => {});

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadItems();
  };

  const openItem = async (n) => {
    if (!n.read) {
      await userApi.markNotifRead(n.id).catch(() => {});
      setUnread((c) => Math.max(0, c - 1));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.url) navigate(n.url);
  };

  const markAll = async () => {
    await userApi.markAllNotifRead().catch(() => {});
    setUnread(0);
    setItems((list) => list.map((x) => ({ ...x, read: true })));
  };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notif-bell-btn" onClick={toggle} className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 transition-colors" aria-label="Notificaciones">
        <Bell className="w-5 h-5 text-white" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#E23B3B] text-white text-xs font-extrabold flex items-center justify-center ring-2 ring-navy">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notif-panel" className="fixed left-3 right-3 top-[76px] w-auto sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 sm:max-w-[90vw] rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-black/5">
            <p className="font-extrabold text-[#0A1C30]">Notificaciones</p>
            {items.some((x) => !x.read) && (
              <button data-testid="notif-markall-btn" onClick={markAll} className="inline-flex items-center gap-1 text-xs font-bold text-[#3B7DF6] hover:underline">
                <CheckCheck className="w-3.5 h-3.5" /> Marcar todo
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-black/5">
            {items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => openItem(n)} className={`w-full text-left px-4 py-3 hover:bg-[#F4F6F9] transition-colors ${n.read ? "" : "bg-[#FFFBEB]"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#E9A400] flex-shrink-0" />}
                  <div className={n.read ? "opacity-70" : ""}>
                    <p className="font-bold text-sm text-[#0A1C30] flex items-center gap-1">{n.title}{n.read && <Check className="w-3.5 h-3.5 text-[#22A860]" />}</p>
                    <p className="text-sm text-[#6B7688] leading-snug">{n.body}</p>
                  </div>
                </div>
              </button>
            ))}
            {items.length === 0 && <p className="px-4 py-10 text-center text-sm text-[#6B7688]">No tienes notificaciones.</p>}
          </div>
        </div>
      )}
    </div>
  );
};
